import {
  type OutputGroup,
  type OutputStatus,
  type PageResult,
  type SplitSummary,
} from './types';
import { sanitizeFileName, sourceBaseName, uniqueFileName } from './normalize';

export interface GroupingResult {
  outputs: OutputGroup[];
  unresolvedPages: number[];
  summary: SplitSummary;
  needsReviewPageNumbers: number[];
}

/**
 * Classify and group page recognition results into output PDFs:
 *
 *  - Single-item single-piece orders (one page per tracking number) are
 *    aggregated into `<source>_single.pdf` in original page order.
 *  - Single-item multi-piece and multi-item mixed orders are grouped by
 *    PO number: every label page of the same order goes into one
 *    `<PO>.pdf` (multiple labels per PDF). Tracking-number grouping is only
 *    used as a fallback when a page carries no PO.
 *  - Pages without a valid tracking number go to `unresolved-pages.pdf`.
 */
export function groupAndClassify(
  pageResults: PageResult[],
  sourceFileName: string,
  startedAtMs: number,
): GroupingResult {
  const warnings: string[] = [];
  const unresolved: PageResult[] = [];
  const resolved: PageResult[] = [];

  for (const page of pageResults) {
    if (page.tracking && page.status !== 'unresolved') {
      resolved.push(page);
    } else {
      unresolved.push(page);
    }
  }

  // Index pages by PO and tracking so multi-piece orders can be detected.
  const byPO = new Map<string, PageResult[]>();
  const byTracking = new Map<string, PageResult[]>();
  for (const page of resolved) {
    if (page.po) {
      const list = byPO.get(page.po) ?? [];
      list.push(page);
      byPO.set(page.po, list);
    }
    if (page.tracking) {
      const list = byTracking.get(page.tracking) ?? [];
      list.push(page);
      byTracking.set(page.tracking, list);
    }
  }

  /**
   * A page belongs to a multi-piece / mixed order when its package indicator
   * says total > 1, when its PO appears on several pages, or (fallback) when
   * its tracking number appears on several pages.
   */
  const isMultiPiece = (page: PageResult): boolean => {
    const indicator = orderIndicatorOf(page);
    if (indicator && indicator.total > 1) return true;
    if (page.po && (byPO.get(page.po)?.length ?? 0) > 1) return true;
    if (page.tracking && (byTracking.get(page.tracking)?.length ?? 0) > 1) return true;
    return false;
  };

  const multiPages = resolved.filter(isMultiPiece);
  const multiPageNumbers = new Set(multiPages.map((p) => p.pageNumber));
  const singlePages = resolved.filter((p) => !multiPageNumbers.has(p.pageNumber));

  // Group multi-piece / mixed pages by order key: PO number first, tracking
  // number as a fallback when the page has no PO.
  const byOrderKey = new Map<string, PageResult[]>();
  for (const page of multiPages) {
    const key = page.po ?? page.tracking ?? '';
    const list = byOrderKey.get(key) ?? [];
    list.push(page);
    byOrderKey.set(key, list);
  }

  // Same tracking number under different order keys -> needs review.
  const trackingOrderKeys = new Map<string, Set<string>>();
  for (const [key, pages] of byOrderKey) {
    for (const page of pages) {
      if (!page.tracking) continue;
      const keys = trackingOrderKeys.get(page.tracking) ?? new Set<string>();
      keys.add(key);
      trackingOrderKeys.set(page.tracking, keys);
    }
  }
  const conflictingTrackings = new Set<string>();
  for (const [tracking, keys] of trackingOrderKeys) {
    if (keys.size > 1) conflictingTrackings.add(tracking);
  }

  const outputs: OutputGroup[] = [];
  const usedNames = new Set<string>();

  for (const [key, pages] of byOrderKey) {
    const sorted = [...pages].sort((a, b) => a.pageNumber - b.pageNumber);
    const isPoGroup = sorted.every((p) => p.po === key);
    const orderNumbers = collectDistinctPOs(sorted);
    const groupWarnings: string[] = [];
    if (orderNumbers.length === 0) {
      groupWarnings.push('页面未关联到 PO Number');
    }
    if (pages.some((p) => p.warnings.some((w) => w.includes('多个候选')))) {
      groupWarnings.push('页面识别到多个 Tracking Number 候选，请人工复核');
    }
    const shared = new Set<string>();
    for (const page of sorted) {
      if (page.tracking && conflictingTrackings.has(page.tracking)) shared.add(page.tracking);
    }
    if (shared.size > 0) {
      groupWarnings.push(`同一 Tracking Number 关联多个订单（${[...shared].join(', ')}），请人工复核`);
    }
    const status: OutputStatus = groupWarnings.some((w) => w.includes('请人工复核'))
      ? 'needs_review'
      : 'success';
    const distinctTrackings = new Set(sorted.map((p) => p.tracking).filter(Boolean));
    const fileName = uniqueFileName(`${sanitizeFileName(key)}.pdf`, usedNames);
    usedNames.add(fileName);
    outputs.push({
      fileName,
      trackingNumber: isPoGroup
        ? distinctTrackings.size === 1
          ? [...distinctTrackings][0]
          : null
        : key,
      orderNumbers,
      pageNumbers: sorted.map((p) => p.pageNumber),
      type: isPoGroup ? 'po_group' : 'tracking_group',
      status,
      warnings: groupWarnings,
    });
  }

  // Single-item single-piece aggregate.
  if (singlePages.length > 0) {
    const sorted = singlePages.sort((a, b) => a.pageNumber - b.pageNumber);
    const orderNumbers = collectDistinctPOs(sorted);
    const base = sourceBaseName(sourceFileName);
    const aggregateWarnings: string[] = [];
    const allSingle = sorted.every((p) => p.trackingCorrected === false);
    if (orderNumbers.length === 0) aggregateWarnings.push('部分页面未关联到 PO Number');
    if (!allSingle) aggregateWarnings.push('部分 Tracking Number 经修正后通过格式校验');
    const status: OutputStatus = aggregateWarnings.some((w) => w.includes('需复核'))
      ? 'needs_review'
      : 'success';
    const fileName = uniqueFileName(`${base}_single.pdf`, usedNames);
    usedNames.add(fileName);
    outputs.push({
      fileName,
      trackingNumber: null,
      orderNumbers,
      pageNumbers: sorted.map((p) => p.pageNumber),
      type: 'single_item_single_piece',
      status,
      warnings: aggregateWarnings,
    });
  }

  outputs.sort((a, b) => a.pageNumbers[0] - b.pageNumbers[0]);

  const unresolvedPageNumbers = unresolved
    .map((p) => p.pageNumber)
    .sort((a, b) => a - b);
  const unresolvedOutput: OutputGroup | null =
    unresolvedPageNumbers.length > 0
      ? {
          fileName: uniqueFileName('unresolved-pages.pdf', usedNames),
          trackingNumber: null,
          orderNumbers: [],
          pageNumbers: unresolvedPageNumbers,
          type: 'tracking_group',
          status: 'needs_review',
          warnings: ['这些页面无法识别有效的 Tracking Number，已保留原始页面待人工处理'],
        }
      : null;
  if (unresolvedOutput) {
    usedNames.add(unresolvedOutput.fileName);
    outputs.push(unresolvedOutput);
  }

  // Page conservation: every page belongs to exactly one output, no duplicates.
  const assigned = new Set<number>();
  let conservationOk = true;
  for (const output of outputs) {
    for (const page of output.pageNumbers) {
      if (assigned.has(page)) conservationOk = false;
      assigned.add(page);
    }
  }
  const total = pageResults.length;
  conservationOk =
    conservationOk && assigned.size === total && pageResults.every((p) => assigned.has(p.pageNumber));
  if (!conservationOk) {
    warnings.push('页面守恒校验失败：存在缺失或重复的页面，请勿下载使用结果');
  }

  const needsReviewPageNumbers = pageResults
    .filter((p) => p.status === 'needs_review' || p.status === 'unresolved')
    .map((p) => p.pageNumber)
    .sort((a, b) => a - b);

  const successfulPages = resolved.length;
  const summary: SplitSummary = {
    sourceFileName,
    sourcePageCount: total,
    processedPageCount: successfulPages,
    unresolvedPageCount: unresolved.length,
    singlePageCount: singlePages.length,
    poGroupCount: outputs.filter((o) => o.type === 'po_group').length,
    outputFileCount: outputs.length,
    needsReviewCount: needsReviewPageNumbers.length,
    elapsedMs: Date.now() - startedAtMs,
    trackingRecognitionRate: total > 0 ? successfulPages / total : 0,
    pageConservationOk: conservationOk,
    warnings,
  };

  return { outputs, unresolvedPages: unresolvedPageNumbers, summary, needsReviewPageNumbers };
}

function collectDistinctPOs(pages: PageResult[]): string[] {
  const set = new Set<string>();
  for (const page of pages) {
    if (page.po) set.add(page.po);
  }
  return [...set];
}

/** Parse an "x OF y" package indicator, e.g. "1 OF 2". */
function orderIndicatorOf(page: PageResult): { current: number; total: number } | null {
  const m = (page.orderIndicator ?? '').match(/^(\d{1,3})\s*OF\s*(\d{1,3})$/i);
  if (!m) return null;
  return { current: Number(m[1]), total: Number(m[2]) };
}

/** Build per-page results with statuses and warnings from OCR fields. */
export function buildPageResults(
  fields: Array<{
    pageNumber: number;
    tracking: { value: string | null; corrected: boolean; candidates: string[]; raw: string | null; confidence: number | null };
    po: string | null;
    poRaw: string | null;
    orderIndicator: string | null;
  }>,
): PageResult[] {
  return fields.map((f) => {
    const warnings: string[] = [];
    let status: PageResult['status'] = 'success';
    let tracking: string | null = f.tracking.value;

    if (!tracking) {
      status = 'unresolved';
      warnings.push('未识别到符合格式的 Tracking Number');
    } else if (f.tracking.candidates.length > 1) {
      status = 'needs_review';
      warnings.push(`页面识别到多个 Tracking Number 候选：${f.tracking.candidates.join(', ')}`);
    }
    if (tracking && f.tracking.corrected) {
      warnings.push(`Tracking Number 经修正后通过格式校验（原始识别：${f.tracking.raw ?? ''}）`);
    }
    if (!f.po) {
      warnings.push('未识别到 PO Number / Trx Ref No.');
      if (status === 'success') status = 'needs_review';
    }

    return {
      pageNumber: f.pageNumber,
      tracking,
      trackingRaw: f.tracking.raw,
      trackingCandidates: f.tracking.candidates,
      trackingConfidence: f.tracking.confidence,
      trackingCorrected: f.tracking.corrected,
      po: f.po,
      poRaw: f.poRaw,
      orderIndicator: f.orderIndicator,
      status,
      warnings,
    };
  });
}
